import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["isRanking"],
    _excluded2 = ["width", "children", "isSortable", "sortOrder", "isFixedSize", "shouldTruncate", "onClick", "style"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

/** @jsx jsx */
import { forwardRef } from 'react';
import { css, jsx } from '@emotion/core';
import { B100, N30A } from '@atlaskit/theme/colors';
import { useGlobalTheme } from '@atlaskit/theme/components';
import { gridSize as getGridSize } from '@atlaskit/theme/constants';
import { ASC, DESC } from '../internal/constants';
import { arrow, head, MSThemeColors } from '../theme';
import { cellStyles, fixedSizeTruncateStyles, getTruncationStyleVars, overflowTruncateStyles, truncationWidthStyles } from './constants';
var gridSize = getGridSize();
var CSS_VAR_TEXT_COLOR = '--local-dynamic-table-text-color';
var rankingStyles = css({
  display: 'block'
});

var getHeadStyles = function getHeadStyles(theme) {
  return css({
    borderBottom: "2px solid ".concat("var(--ds-border, ".concat(head.borderColor({
      theme: theme
    }), ")"))
  });
};

export var Head = function Head(_ref) {
  var isRanking = _ref.isRanking,
      props = _objectWithoutProperties(_ref, _excluded);

  var theme = useGlobalTheme();
  return jsx("thead", _extends({
    css: [getHeadStyles(theme), isRanking && rankingStyles]
  }, props));
};
var headCellStyles = css([cellStyles, {
  border: 'none',
  boxSizing: 'border-box',
  fontSize: '12px',
  fontWeight: 600,
  position: 'relative',
  textAlign: 'left',
  verticalAlign: 'top',
  color: "var(--ds-text-subtlest, ".concat("var(".concat(CSS_VAR_TEXT_COLOR, ")"), ")"),
  '&:focus': {
    outline: "solid 2px ".concat("var(--ds-border-focused, ".concat(B100, ")"))
  }
}]); // this needs to be made static: https://product-fabric.atlassian.net/browse/DSP-2011

export var getArrowStyles = function getArrowStyles(isSortable, sortOrder, theme) {
  if (!isSortable) {
    return '';
  }

  var pseudoBaseStyles = {
    border: '3px solid transparent',
    display: 'block',
    height: 0,
    right: "-".concat(gridSize, "px"),
    width: 0,
    '@media (forced-colors: active)': {
      border: "3px solid ".concat(MSThemeColors.Background)
    }
  };
  return css({
    '& > span': {
      position: 'relative',
      '&::before': _objectSpread(_objectSpread({}, pseudoBaseStyles), {}, {
        position: 'absolute',
        borderBottom: "3px solid ".concat(sortOrder === ASC ? arrow.selectedColor({
          theme: theme
        }) : arrow.defaultColor({
          theme: theme
        })),
        bottom: '8px',
        content: '""'
      }),
      '&::after': _objectSpread(_objectSpread({}, pseudoBaseStyles), {}, {
        position: 'absolute',
        borderTop: "3px solid ".concat(sortOrder === DESC ? arrow.selectedColor({
          theme: theme
        }) : arrow.defaultColor({
          theme: theme
        })),
        bottom: 0,
        content: '""'
      })
    },
    '&:hover > span': {
      '&::before': {
        borderBottom: "3px solid\n          ".concat(sortOrder === ASC ? arrow.selectedColor({
          theme: theme
        }) : arrow.hoverColor({
          theme: theme
        }))
      },
      '&::after': {
        borderTop: "3px solid\n          ".concat(sortOrder === DESC ? arrow.selectedColor({
          theme: theme
        }) : arrow.hoverColor({
          theme: theme
        }))
      }
    },
    '@media (forced-colors: active)': {
      '& > span': {
        '&::before': {
          borderBottom: "3px solid\n            ".concat(sortOrder === ASC ? MSThemeColors.SelectedBackground : MSThemeColors.Text)
        },
        '&::after': {
          borderTop: "3px solid\n            ".concat(sortOrder === DESC ? MSThemeColors.SelectedBackground : MSThemeColors.Text)
        }
      },
      '&:hover > span': {
        '&::before': {
          borderBottom: "3px solid\n            ".concat(sortOrder === ASC ? MSThemeColors.SelectedBackground : MSThemeColors.Text)
        },
        '&::after': {
          borderTop: "3px solid\n            ".concat(sortOrder === DESC ? MSThemeColors.SelectedBackground : MSThemeColors.Text)
        }
      }
    }
  });
};
var onClickStyles = css({
  '&:hover': {
    cursor: 'pointer',
    backgroundColor: "var(--ds-background-neutral-hovered, ".concat(N30A, ")")
  }
});
export var HeadCell = /*#__PURE__*/forwardRef(function (_ref2, ref) {
  var width = _ref2.width,
      children = _ref2.children,
      isSortable = _ref2.isSortable,
      sortOrder = _ref2.sortOrder,
      isFixedSize = _ref2.isFixedSize,
      shouldTruncate = _ref2.shouldTruncate,
      onClick = _ref2.onClick,
      style = _ref2.style,
      rest = _objectWithoutProperties(_ref2, _excluded2);

  var theme = useGlobalTheme();

  var mergedStyles = _objectSpread(_objectSpread(_objectSpread({}, style), width && getTruncationStyleVars({
    width: width
  })), {}, _defineProperty({}, CSS_VAR_TEXT_COLOR, head.textColor({
    theme: theme
  })));

  return jsx("th", _extends({
    style: mergedStyles,
    css: [headCellStyles, onClick && onClickStyles, truncationWidthStyles, isFixedSize && shouldTruncate && fixedSizeTruncateStyles, isFixedSize && overflowTruncateStyles, getArrowStyles(isSortable, sortOrder, theme)],
    onClick: onClick,
    ref: ref
  }, rest), children);
});